import { useState, useEffect } from "react";
import { mutate } from "swr";
import axios from "axios";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ErrorMessage from "../../components/common/ErrorMessage";
import TableComponent from "../../components/common/TableComponent";
import {
  generateNoDataValues,
  getValueInCurrency,
  GeneralStatusEnum,
} from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { BackForeColor } from "../../components/common/BackForeColor";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getAdditionalCostStatusName,
  getAdditionalCostStatusBackColor,
  getAdditionalCostStatusForeColor,
  getPaymentToTechnicianRequestFilteringOptions,
  getCurrencyFromSetup,
} from "../../common/EntitiesManagement";
import { PAYMENT_TO_TECHNICIAN_ENDPOINT } from "../../services/config";
import LoadingPanel from "../../components/common/LoadingPanel";
import PaymentToTechnician from "../event/components/PaymentToTechnician";

export default function PaymentToTechnicianRequestList() {
  const {
    config,
    paymentToTechnicianRequestData,
    paymentToTechnicianRequestError,
    pageIndexPaymentToTechnicianRequest,
    setPageIndexPaymentToTechnicianRequest,
    setBasicFilterOptions,
  } = useGlobalContext();

  useEffect(() => {
    setBasicFilterOptions(getPaymentToTechnicianRequestFilteringOptions());
  }, []);

  const normalizedData = Array.isArray(paymentToTechnicianRequestData)
    ? {
        Data: paymentToTechnicianRequestData,
        Count: paymentToTechnicianRequestData.length,
      }
    : paymentToTechnicianRequestData;

  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [paymentModel, setPaymentModel] = useState(null);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);

  useEffect(() => {
    if (paymentToTechnicianRequestError) {
      setErrorMessage(
        paymentToTechnicianRequestError?.message || localize.error_unexpected,
      );
      setShowErrorModal(true);
    }
  }, [paymentToTechnicianRequestError]);

  const getColumnTitles = () => [
    localize.payment_type,
    localize.value,
    localize.status,
    localize.personal,
  ];

  const handleUpdatePayment = async () => {
    try {
      const data = await axios.put(
        `${PAYMENT_TO_TECHNICIAN_ENDPOINT}/${paymentModel.Id}`,
        paymentModel,
        config,
      );
      if (data?.data?.Ok) {
        mutate([
          PAYMENT_TO_TECHNICIAN_ENDPOINT,
          {
            ...config,
            headers: {
              ...config.headers,
              pagination: pageIndexPaymentToTechnicianRequest,
            },
          },
        ]);
        window.location.reload(false);
      } else {
        setErrorMessage(JSON.stringify(data?.data?.Data || data?.data?.data));
        setShowErrorModal(true);
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorModal(true);
    }
    setShowStatusChangeModal(false);
  };

  if (!paymentToTechnicianRequestData && !paymentToTechnicianRequestError) {
    return <LoadingPanel />;
  }

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showStatusChangeModal && paymentModel && (
        <ModalBackground
          isDrawerMode
          showModal={showStatusChangeModal}
          setShowModal={setShowStatusChangeModal}
          title={localize.change_payment_to_technician_status}
          onClickToCancel={() => {
            setShowStatusChangeModal(false);
          }}
          onClickToSubmit={() => {
            handleUpdatePayment();
          }}
          validators={
            paymentModel?.Status === GeneralStatusEnum.Generated ||
            (paymentModel?.Status === GeneralStatusEnum.Rejected &&
              (!paymentModel?.StatusObservation ||
                paymentModel.StatusObservation.length === 0))
          }
          Component={
            <div className="mb-4">
              <PaymentToTechnician
                eventData={
                  paymentModel?.Event ?? { Id: paymentModel?.eventId ?? 0 }
                }
                paymentData={paymentModel}
                setPaymentData={setPaymentModel}
              />
            </div>
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.payment_to_technician_request_title}
            subtitle={localize.payment_to_technician_request_description}
          />
          <SearcherAndAdd hideAddButton />
        </CardContent>
      </Card>

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexPaymentToTechnicianRequest}
            setPageIndex={setPageIndexPaymentToTechnicianRequest}
            dataArray={normalizedData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          normalizedData?.Data?.length > 0
            ? normalizedData.Data.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      hideDeleteButton
                      detailsButtonText={
                        localize.payment_to_technician_request_view
                      }
                      onClickForDetailButton={() => {
                        window.open(
                          `/events/detail/${
                            item.EventId ?? item.eventId
                          }?tab_id=13`,
                          "_blank",
                        );
                      }}
                      hideEditButton={item.Status !== GeneralStatusEnum.Generated}
                      onClickForEditButton={() => {
                        setPaymentModel({
                          ...item,
                          Observation: item.Observation ?? "",
                          StatusObservation: item.StatusObservation ?? "",
                        });
                        setShowStatusChangeModal(true);
                      }}
                    />
                  </div>,
                  <ValueOrNoDataLabel
                    data={
                      item?.PaymentTypeData?.Name ?? item?.PaymentType?.Name
                    }
                  />,
                  <ValueOrNoDataLabel
                    data={getValueInCurrency(
                      getCurrencyFromSetup(),
                      item?.Value,
                    )}
                  />,
                  <BackForeColor
                    backColor={getAdditionalCostStatusBackColor(item.Status)}
                    foreColor={getAdditionalCostStatusForeColor(item.Status)}
                    text={getAdditionalCostStatusName(item.Status)}
                  />,
                  <ValueOrNoDataLabel
                    data={
                      item?.PersonalData
                        ? `${item.PersonalData.Identification ?? ""} - ${
                            item.PersonalData.SurName ?? ""
                          } ${item.PersonalData.LastName ?? ""}`.trim()
                        : undefined
                    }
                  />,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
